import { ComponentProps, FC, Ref } from 'react';
import Image from 'next/image';
import classNames from 'classnames';

import styles from './AddressSuggestionsList.module.scss';
import { Icon } from '../atoms/icon';

interface AddressSuggestionsListProps
  extends Omit<ComponentProps<'ul'>, 'onSelect'> {
  activeIndex?: number;
  getOptionId: (_index: number) => string;
  listRef?: Ref<HTMLUListElement | null>;
  suggestions: google.maps.places.AutocompleteSuggestion[];
  onActiveIndexChange?: (_index: number) => void;
  onSuggestionSelected(
    _suggestion: google.maps.places.AutocompleteSuggestion,
  ): void;
}

const AddressSuggestionsList: FC<AddressSuggestionsListProps> = ({
  activeIndex = -1,
  className,
  getOptionId,
  id,
  listRef,
  onActiveIndexChange,
  onSuggestionSelected,
  suggestions,
  ...props
}) => {
  return (
    <div className={classNames(styles.suggestions, className)}>
      <ul
        {...props}
        aria-label="Address suggestions"
        className={styles.suggestions_list}
        id={id}
        ref={listRef}
        role="listbox"
      >
        {suggestions.map((suggestion, index) => {
          const isActive = index === activeIndex;
          const optionId = getOptionId(index);
          const label = suggestion.placePrediction?.text?.text ?? '';

          return (
            <li
              aria-selected={isActive}
              className={classNames(styles.suggestions_list__item, {
                [styles.suggestions_list__item_active]: isActive,
              })}
              id={optionId}
              key={suggestion.placePrediction?.placeId ?? optionId}
              onClick={event => {
                event.stopPropagation();
                onSuggestionSelected(suggestion);
              }}
              onMouseDown={event => {
                // Prevent input blur before click selection completes
                event.preventDefault();
              }}
              onMouseEnter={() => onActiveIndexChange?.(index)}
              role="option"
            >
              <Icon aria-hidden name="location" />
              <span>{label}</span>
            </li>
          );
        })}
      </ul>
      <Image
        alt="Powered by Google"
        className={styles.powered_by_google}
        height={40}
        src="https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3_hdpi.png"
        width={132}
      />
    </div>
  );
};

export { AddressSuggestionsList };
